import { useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { PurchasesTable, SubscriptionsTable } from '../components/StoreTables';
import { STORE_PRODUCT_KINDS } from '../lib/validate/types';
import {
  environments,
  platforms,
  purchaseStatuses,
  subscriptionStatuses,
  type StoreFilters,
} from '../lib/playerSupport';

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border-2 border-ink px-2 py-1"
    >
      <option value="">any {label.toLowerCase()}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

/** Store purchases and supporter pass subscriptions, for player support (teams-spec section 12). */
export function PurchasesPage() {
  const [kind, setKind] = useState<'purchases' | 'subscriptions'>('purchases');
  const [filters, setFilters] = useState<StoreFilters>({
    playerId: '',
    platform: '',
    environment: '',
    status: '',
    productKind: '',
  });
  const [offset, setOffset] = useState(0);
  const update = (patch: Partial<StoreFilters>) => {
    setFilters({ ...filters, ...patch });
    setOffset(0);
  };
  const statuses = kind === 'purchases' ? purchaseStatuses : subscriptionStatuses;

  return (
    <>
      <PageHeader
        title="Purchases"
        description="Verified store purchases and supporter passes. Sandbox, test and simulated ones are highlighted: they aren't revenue."
      />
      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <select
          aria-label="Kind"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as 'purchases' | 'subscriptions');
            setFilters({ ...filters, status: '', productKind: '' });
            setOffset(0);
          }}
          className="rounded border-2 border-ink px-2 py-1 font-semibold"
        >
          <option value="purchases">Purchases</option>
          <option value="subscriptions">Subscriptions</option>
        </select>
        <Select
          label="Environment"
          value={filters.environment}
          options={environments}
          onChange={(environment) => update({ environment })}
        />
        <Select
          label="Platform"
          value={filters.platform}
          options={platforms}
          onChange={(platform) => update({ platform })}
        />
        <Select
          label="Status"
          value={filters.status}
          options={statuses}
          onChange={(status) => update({ status })}
        />
        {kind === 'purchases' && (
          <Select
            label="Product kind"
            value={filters.productKind ?? ''}
            options={STORE_PRODUCT_KINDS}
            onChange={(productKind) => update({ productKind })}
          />
        )}
        <input
          aria-label="Player id"
          placeholder="player id"
          value={filters.playerId}
          onChange={(e) => update({ playerId: e.target.value.trim() })}
          className="w-80 rounded border-2 border-ink px-2 py-1 font-mono"
        />
      </div>
      {kind === 'purchases' ? (
        <PurchasesTable filters={filters} offset={offset} onOffset={setOffset} />
      ) : (
        <SubscriptionsTable filters={filters} offset={offset} onOffset={setOffset} />
      )}
    </>
  );
}
